import { useState } from 'react';

const PAGES = [
  {
    chapter: 'Chapter I', title: 'The Problem', subtitle: '5G Latency in Robotic Systems',
    content: `In next-generation robotic applications, 5G network delays are not merely inconvenient — they are catastrophic. A 20ms prediction window separates a robot arm that welds precisely from one that destroys a component.

The IP delay in 5G systems follows a complex, triangular pattern driven by Frame Alignment Delay in the RLC layer. This pattern is periodic, partially predictable — yet no existing deployed system exploited this regularity.

By studying packet traces across IP, RLC, MAC, and Physical layers, we identified the primary cause of this triangular Delay vs. SN pattern: Frame Alignment Delay in the RLC scheduler. This discovery became the foundation for our predictive model.

We set out to change that — and succeeded.`,
    stat: { value: '20ms', label: 'Critical Threshold' }, icon: '⚡',
  },
  {
    chapter: 'Chapter II', title: 'The Dataset', subtitle: 'KTH ExPECA Testbed, Sweden',
    content: `The ExPECA testbed at KTH Royal Institute of Technology represents one of the world's most advanced 5G research environments. Real packet traces — not simulations — spanning IP, RLC, MAC and Physical layers captured under live 5G NR radio conditions.

40,000 records of IP delay measurements in milliseconds. Two files: TR1.csv for training, TE1.csv for held-out evaluation. Features include IP delay timestamps (ip.in and ip.out), sequence numbers, RLC layer timing, and packet trace metadata.

The triangular delay pattern — caused by frame alignment in the RLC scheduler — became the key insight that no prior time-series model had exploited. The periodicity of this pattern makes it fundamentally predictable.`,
    stat: { value: '40K+', label: 'Packet Records' }, icon: '📡',
  },
  {
    chapter: 'Chapter III', title: 'Failed Approaches', subtitle: 'The Road Not Taken',
    content: `Four approaches were evaluated before the breakthrough. Each failure taught a critical lesson.

ARIMA (68%): Classical statistics cannot capture the non-linear temporal dependencies in 5G delay patterns. The stationarity assumptions fail catastrophically against the periodic RLC frame structure.

Pure Recursive LSTM (88%): Each predicted value feeds back as input, compounding error until the signal diverges — catastrophic phase drift over sequences longer than ~30 steps.

Multi-Step Direct (82%): Improved accuracy but lost grounding in real data, reducing adaptability to sudden network condition changes.

Each failure refined our understanding of what the hybrid approach must accomplish.`,
    stat: { value: '3×', label: 'Failures Before Success' }, icon: '🔬',
  },
  {
    chapter: 'Chapter IV', title: 'The Breakthrough', subtitle: 'Hybrid Block-Wise Recursive LSTM',
    content: `The key insight: predict in small blocks of 10, then reset with real data before the next block. This hybrid approach eliminates error accumulation while maintaining the temporal context that makes LSTM powerful.

Architecture: 50 LSTM units → Dense(1). Sequence length: 30 timesteps. The model sees 30 real samples, predicts 10 future values, advances 10 steps using real observations, and repeats indefinitely without drift.

The mathematical guarantee: error accumulation is bounded by a maximum window of 10 steps, after which real data resets the prediction context. Compared to pure recursive LSTM where error grows O(n), our hybrid bounds error to O(10) — constant regardless of sequence length.

This is why we achieve R²=0.9067 where pure recursive degrades to 0.78 over long sequences.`,
    stat: { value: '94.68%', label: 'Final Accuracy' }, icon: '🧠',
  },
  {
    chapter: 'Chapter V', title: 'The Deployment', subtitle: 'From Research to Production',
    content: `Converting a working Keras model to a deployable API revealed a critical constraint: Render's free tier provides 512MB RAM. TensorFlow-CPU requires ~450MB just to import — leaving virtually nothing for actual inference operations.

The solution: ONNX Runtime. Converting the trained model via tf2onnx reduced the inference memory footprint from 450MB to just 80MB — a 5.6× reduction. Identical numerical outputs, 5.6× less RAM, 3× faster inference.

Conversion pipeline: .keras → SavedModel (model.export) → python -m tf2onnx.convert --saved-model → .onnx. The critical step was using the CLI converter rather than the Python API, which fails on Keras 3 models due to tensor name resolution issues.

The model now runs reliably on free cloud infrastructure.`,
    stat: { value: '5.6×', label: 'RAM Reduction' }, icon: '🚀',
  },
  {
    chapter: 'Chapter VI', title: 'The Results', subtitle: 'Benchmarks & Metrics on TE1.csv',
    content: `Evaluated on the held-out TE1.csv dataset — packet traces the model never saw during training — the Hybrid LSTM outperformed every baseline across all metrics.

R² Score: 0.9067 — the model explains 90.67% of variance in IP delay. This is exceptional for a real-world wireless network metric subject to physical-layer randomness.

MAE: 0.2009ms — average error less than a quarter millisecond. RMSE: 0.4414ms. Precision: 1.0000 (no false positives above the 20ms threshold). Recall: 0.7766. Accuracy: 94.68%.

The triangular delay pattern — once considered unpredictable background noise — is now quantifiably foreseeable. This transforms reactive network management into proactive control.`,
    stat: { value: 'R²=0.91', label: 'Variance Explained' }, icon: '📊',
  },
];

export default function ResearchBook({ dark }) {
  const [current, setCurrent] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const [flipDir, setFlipDir] = useState('next');
  const [setOutgoing] = useState(null);

  const accent = dark ? '#00d4ff' : '#0078c8';
  const pageBg = dark ? '#0d1929' : '#fffdf7';
  const text1 = dark ? '#e8f4ff' : '#1a0e00';
  const text2 = dark ? '#8ab4d4' : '#4a3a20';
  const text3 = dark ? '#4a7090' : '#8a7a60';
  const border = dark ? 'rgba(0,212,255,0.15)' : 'rgba(100,80,40,0.2)';
  const spineC = dark ? '#010810' : '#ddd8cc';
  const shadowC = dark ? 'rgba(0,0,0,0.7)' : 'rgba(100,80,40,0.3)';
  const ruleC = dark ? 'rgba(0,212,255,0.04)' : 'rgba(100,80,40,0.06)';

  const goTo = (dir, targetIdx) => {
    if (flipping) return;
    const next = targetIdx !== undefined ? targetIdx : (dir === 'next' ? current+1 : current-1);
    if (next < 0 || next >= PAGES.length) return;
    const actualDir = next > current ? 'next' : 'prev';
    setOutgoing(current);
    setFlipDir(actualDir);
    setFlipping(true);
    setTimeout(() => { setCurrent(next); setFlipping(false); setOutgoing(null); }, 700);
  };

  const page = PAGES[current];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Georgia&family=Inter:wght@400;600;700&family=Orbitron:wght@700;900&display=swap');
        .bwrap{display:flex;flex-direction:column;align-items:center;padding:32px 0 56px;}
        .bouter{position:relative;width:100%;max-width:820px;perspective:1800px;}

        /* barrel flip: book rotates on X axis (like a barrel roll) */
        .book{
          position:relative;display:flex;width:100%;min-height:480px;
          border-radius:3px 8px 8px 3px;
          box-shadow:-8px 0 24px ${shadowC},0 8px 48px ${shadowC},8px 0 16px ${shadowC};
          transform-style:preserve-3d;
        }
        .book.flip-next{animation:barrelNext 0.7s cubic-bezier(0.4,0,0.2,1) forwards;}
        .book.flip-prev{animation:barrelPrev 0.7s cubic-bezier(0.4,0,0.2,1) forwards;}

        @keyframes barrelNext{
          0%{transform:rotateX(0deg) rotateY(0deg);}
          25%{transform:rotateX(-12deg) rotateY(-6deg) translateY(-8px);}
          50%{transform:rotateX(180deg) rotateY(0deg) translateY(-12px);}
          75%{transform:rotateX(168deg) rotateY(6deg) translateY(-4px);}
          100%{transform:rotateX(360deg) rotateY(0deg) translateY(0px);}
        }
        @keyframes barrelPrev{
          0%{transform:rotateX(0deg) rotateY(0deg);}
          25%{transform:rotateX(12deg) rotateY(6deg) translateY(-8px);}
          50%{transform:rotateX(-180deg) rotateY(0deg) translateY(-12px);}
          75%{transform:rotateX(-168deg) rotateY(-6deg) translateY(-4px);}
          100%{transform:rotateX(-360deg) rotateY(0deg) translateY(0px);}
        }

        .book-spine{
          width:34px;background:${spineC};border-radius:3px 0 0 3px;
          flex-shrink:0;display:flex;align-items:center;justify-content:center;
          border-right:2px solid ${border};position:relative;overflow:hidden;
        }
        .book-spine::before{
          content:'5G DELAY RESEARCH';writing-mode:vertical-rl;text-orientation:mixed;
          transform:rotate(180deg);font-size:8px;font-weight:700;letter-spacing:3px;
          color:${text3};font-family:'Inter',sans-serif;text-transform:uppercase;opacity:0.55;
        }
        .book-spine::after{
          content:'';position:absolute;top:0;bottom:0;right:0;width:8px;
          background:linear-gradient(90deg,${dark?'rgba(0,0,0,0.35)':'rgba(0,0,0,0.1)'},transparent);
        }

        .book-page{
          flex:1;background:${pageBg};padding:44px 48px 56px;
          display:flex;flex-direction:column;position:relative;overflow:hidden;
          border-radius:0 8px 8px 0;
        }
        /* ruled lines */
        .book-page::before{
          content:'';position:absolute;top:0;left:82px;right:0;bottom:0;
          background-image:repeating-linear-gradient(transparent,transparent 31px,${ruleC} 31px,${ruleC} 32px);
          pointer-events:none;
        }
        /* red margin */
        .book-page::after{
          content:'';position:absolute;top:0;left:80px;bottom:0;width:1.5px;
          background:${dark?'rgba(255,70,70,0.15)':'rgba(200,50,50,0.2)'};
        }

        /* Page fold effect */
        .fold{
          position:absolute;bottom:0;right:0;width:0;height:0;
          border-style:solid;border-width:0 0 40px 40px;
          border-color:transparent transparent ${dark?'#050d1c':'#ece8df'} transparent;
          filter:drop-shadow(-2px -2px 4px ${shadowC});
          transition:border-width 0.2s;
        }
        .book:hover .fold{border-width:0 0 52px 52px;}

        /* watermark */
        .watermark{
          position:absolute;bottom:60px;right:60px;
          font-family:'Orbitron',monospace;font-size:52px;font-weight:900;
          color:${dark?'rgba(0,212,255,0.03)':'rgba(0,0,0,0.03)'};
          pointer-events:none;user-select:none;line-height:1;
          transform:rotate(-15deg);
        }

        .ch-label{font-size:10px;font-weight:700;letter-spacing:3px;color:${accent};text-transform:uppercase;margin-bottom:8px;font-family:'Inter',sans-serif;position:relative;z-index:1;}
        .pg-title{font-family:'Orbitron',monospace;font-size:clamp(20px,3vw,30px);font-weight:900;color:${text1};margin-bottom:4px;position:relative;z-index:1;line-height:1.1;}
        .pg-sub{font-size:13px;color:${text3};margin-bottom:20px;font-style:italic;font-family:'Georgia',serif;position:relative;z-index:1;}
        .pg-div{height:1px;background:linear-gradient(90deg,${accent}55,transparent);margin-bottom:20px;position:relative;z-index:1;}
        .pg-body{
          font-family:'Georgia',serif;font-size:14.5px;line-height:2;
          color:${text2};flex:1;position:relative;z-index:1;
          white-space:pre-wrap;
        }
        .pg-stat{
          display:flex;align-items:center;gap:16px;margin-top:24px;
          padding:14px 18px;border:1px solid ${border};border-radius:4px;
          background:${dark?'rgba(0,212,255,0.04)':'rgba(100,80,40,0.04)'};
          position:relative;z-index:1;
        }
        .st-icon{font-size:26px;}
        .st-val{font-family:'Orbitron',monospace;font-size:22px;font-weight:900;color:${accent};}
        .st-lbl{font-size:10px;color:${text3};text-transform:uppercase;letter-spacing:1px;font-family:'Inter',sans-serif;}
        .pg-num{position:absolute;bottom:20px;right:48px;font-size:11px;color:${text3};font-family:'Georgia',serif;font-style:italic;z-index:1;}

        .bnav{display:flex;align-items:center;gap:20px;margin-top:24px;}
        .nbtn{
          font-family:'Inter',sans-serif;font-size:11px;font-weight:700;
          letter-spacing:1.5px;text-transform:uppercase;
          background:transparent;color:${accent};
          border:1px solid ${border};padding:9px 22px;cursor:pointer;
          border-radius:2px;transition:all 0.2s;display:flex;align-items:center;gap:7px;
        }
        .nbtn:hover:not(:disabled){background:${dark?'rgba(0,212,255,0.08)':'rgba(0,120,200,0.08)'};border-color:${accent};}
        .nbtn:disabled{opacity:0.22;cursor:not-allowed;}
        .pdots{display:flex;gap:7px;align-items:center;}
        .pd{width:6px;height:6px;border-radius:50%;background:${border};transition:all 0.3s;cursor:pointer;}
        .pd.on{background:${accent};box-shadow:0 0 8px ${accent};width:22px;border-radius:3px;}
        .pd:hover:not(.on){background:${dark?'rgba(0,212,255,0.4)':'rgba(0,120,200,0.4)'};}
      `}</style>

      <div className="bwrap">
        <div style={{fontFamily:"'Inter',sans-serif",fontSize:11,fontWeight:700,letterSpacing:3,color:text3,textTransform:'uppercase',marginBottom:24,display:'flex',alignItems:'center',gap:12,width:'100%',maxWidth:820}}>
          <span style={{flex:1,height:1,background:border,display:'block'}}/>
          Research Journal
          <span style={{flex:1,height:1,background:border,display:'block'}}/>
        </div>

        <div className="bouter">
          <div className={`book${flipping ? (flipDir==='next'?' flip-next':' flip-prev') : ''}`}>
            <div className="book-spine"/>
            <div className="book-page">
              <div className="watermark">{page.icon}</div>
              <div className="ch-label">{page.chapter}</div>
              <div className="pg-title">{page.title}</div>
              <div className="pg-sub">{page.subtitle}</div>
              <div className="pg-div"/>
              <div className="pg-body">{page.content}</div>
              <div className="pg-stat">
                <div className="st-icon">{page.icon}</div>
                <div>
                  <div className="st-val">{page.stat.value}</div>
                  <div className="st-lbl">{page.stat.label}</div>
                </div>
              </div>
              <div className="pg-num">{current+1} / {PAGES.length}</div>
              <div className="fold"/>
            </div>
          </div>
        </div>

        <div className="bnav">
          <button className="nbtn" onClick={()=>goTo('prev')} disabled={current===0||flipping}>← Prev</button>
          <div className="pdots">
            {PAGES.map((_,i)=>(
              <div key={i} className={`pd${i===current?' on':''}`} onClick={()=>goTo(null,i)}/>
            ))}
          </div>
          <button className="nbtn" onClick={()=>goTo('next')} disabled={current===PAGES.length-1||flipping}>Next →</button>
        </div>
      </div>
    </>
  );
}
